import { EventOption, RuleActionOption, RuleConditionOption } from 'app/core/components/automations/automations-grid';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { autoinject, bindable, observable } from 'aurelia-framework';
import { deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { guid } from 'app/shared/lib/guid';
import { RuleCondition } from 'app/core/components/automations/save-automation';
import { microTask } from 'app/shared/lib/decorators';
import * as Dom from 'app/shared/lib/dom';

@autoinject()
export class SaveCondition extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable
    public options: RuleConditionOption[] = [];

    @bindable
    protected actions: RuleActionOption[] = [];

    @bindable
    protected event?: EventOption;

    @observable
    public selectedProperty: RuleConditionOption | null = null;

    @observable
    public condition: RuleCondition | null = null;

    protected selectedOptions: any[] = [];
    protected previousSelectedOptions: any[] = [];
    protected selectedSubOptions: any[] = [];
    protected previousSelectedSubOptions: any[] = [];

    protected showInactiveOptions: boolean = false;

    protected id: guid = guid();

    public submittedChanges: boolean = false;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Element References ***************************************////


    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        if(!this.selectedProperty?.options) return true;
        const { optionLevel2, subOptionLevel2 } = this.selectedProperty;
        if(!optionLevel2) return true;
        // if counts are different, no need to check individual selections
        if(this.previousSelectedOptions.length !== this.selectedOptions.length || this.previousSelectedSubOptions.length !== this.selectedSubOptions.length) {
            this.submittedChanges = true;
        } else {
            let allOptionsSame = true;
            for(let index = 0; index < this.previousSelectedOptions.length; index++) {
                // if a previous selection is not found among the current selections, a change has been made
                const optionFound = this.selectedOptions.find(o => o[optionLevel2] === this.previousSelectedOptions[index][optionLevel2]);
                if(!optionFound) {
                    allOptionsSame = false;
                    break;
                }
            }
            // if no changes found at the option level and suboptions exist, check for changes on the suboption level
            if(allOptionsSame && subOptionLevel2) {
                for(let index = 0; index < this.previousSelectedSubOptions.length; index++) {
                    const optionFound = this.selectedSubOptions.find(o => o[subOptionLevel2] === this.previousSelectedSubOptions[index][subOptionLevel2]);
                    if(!optionFound) {
                        allOptionsSame = false;
                        break;
                    }
                }
            }
            this.submittedChanges = !allOptionsSame;
        }
        return this.submittedChanges;
    }

    public reset(): void {
        this.hasChanges();
        this.selectedProperty = null;
        this.condition = null;
        this.clearSelectedOptions();
    }

    public async submit(): Promise<boolean> {
        if(!this.selectedProperty) return false;
        this.condition = {
            option: this.selectedProperty,
            optionSelections: this.selectedOptions
        };

        return this.convertSelectionsIntoExpression();
    }

    ////****************** Events ***************************************************////
    protected selectedPropertyChanged(): void {
        if(!this.selectedProperty?.$used) {
            this.showInactiveOptions = false;
            this.clearSelectedOptions();
            this.selectedProperty?.options.forEach(option => option.$selected = false);
        } else {
            // restore selected Options (check checkboxes, expand subchildren)
            this.selectedProperty?.options.forEach((option, index) => {
                option.$expanded = false;
                if(!option.$selected) return;
                this.selectedOptions.push(option);
                if(!this.selectedProperty?.subOptionProperty) return;
                this.optionExpandedToggle(index);
                const subOptions = option[this.selectedProperty?.subOptionProperty];
                if(!subOptions) return;
                subOptions.forEach((so: any) => {
                    if(so.$selected) this.selectedSubOptions.push(so);
                });
            });

            // toggle to show inactive options if any inactive options are pre-selected
            this.showInactiveOptions = this.selectedOptions && !!this.selectedOptions.some(o => !o.isActive && o.$selected) || this.selectedSubOptions && !!this.selectedSubOptions.some(so => !so.isActive && so.$selected);

            // store current selections to watch for changes
            this.previousSelectedOptions = deepClone(this.selectedOptions);
            this.previousSelectedSubOptions = deepClone(this.selectedSubOptions);
            this.hasChanges();
        }
    }

    protected conditionChanged(): void {
        const foundSelectedProperty = this.options.find(o => o.translatedDisplay === this.condition?.option?.translatedDisplay);
        if(foundSelectedProperty && !this.selectedProperty) this.selectedProperty = deepClone(foundSelectedProperty);
    }

    @microTask
    protected optionExpandedToggle(index: number): void {
        const pivotArrow = document.querySelector(`#expand-${index}`);
        if(!pivotArrow) return;
        let option: HTMLElement | null | undefined = pivotArrow.parentElement?.parentElement;
        if(!option || !this.selectedProperty) return;

        let subOptionsList: HTMLElement | null = option.querySelector('.sub-options-list');

        if(!subOptionsList) return;

        // reveal or hide sub options based on pivot arrow's current direction
        if(pivotArrow.classList.contains('fa-angle-right')) {
            pivotArrow.classList.remove('fa-angle-right');
            pivotArrow.classList.add('fa-angle-down');
            subOptionsList.classList.remove('sub-options-list-hide');
            option.classList.add('option-container-expanded');
        } else {
            pivotArrow.classList.remove('fa-angle-down');
            pivotArrow.classList.add('fa-angle-right');
            subOptionsList.classList.add('sub-options-list-hide');
            option.classList.remove('option-container-expanded');
        }
        this.selectedProperty.options[index].$expanded = !this.selectedProperty.options[index].$expanded;
    }

    protected optionCheckedChanged(option: any | null, checked: boolean, checkbox: HTMLElement, index?: number): void {
        if(!option) return;

        // if checked, add option to selection
        if(checked && !this.selectedOptions.includes(option)) this.selectedOptions.push(option);
        // if unchecked, remove option from selection
        if(!checked && this.selectedOptions.includes(option)) this.selectedOptions = this.selectedOptions.filter(o => o !== option);

        // manage suboption selections
        const foundOption = this.selectedProperty?.options.find(o => o === option);
        if(foundOption && this.selectedProperty?.subOptionProperty) {
            // if option unchecked, uncheck all suboptions
            if(!checked) {
                this.selectedSubOptions = this.selectedSubOptions.filter(so => !foundOption[this.selectedProperty?.subOptionProperty!].includes(so));
            } else {
                // otherwise, expand suboptions and select them all
                this.selectedSubOptions.push(...foundOption[this.selectedProperty.subOptionProperty].filter((so: any) => so.isActive || this.showInactiveOptions));
                if(checkbox.parentElement?.parentElement?.parentElement) {
                    const pivotArrow: HTMLAnchorElement | null = checkbox.parentElement.parentElement.parentElement.querySelector('.pivot-arrow');
                    if(pivotArrow && pivotArrow.classList.contains('fa-angle-right') && !isNullOrUndefined(index)) this.optionExpandedToggle(index);
                }
            }
        }

        this.hasChanges();
    }

    protected subOptionCheckedChanged(option: any | null, subOption: any | null, checked: boolean, checkbox: HTMLElement): void {
        if(!option || !subOption) return;

        let optionContainer = Dom.getParentByClassName(checkbox, 'option-container');
        let optionCheckbox = optionContainer?.querySelector('input');

        // if suboption checked without option being checked, check option as well and add to selection
        if(checked && !this.selectedOptions.includes(option)) {
            this.selectedOptions.push(option);
            if(optionCheckbox) optionCheckbox!.checked = checked;
        }

        // if suboption checked, add it to selection
        if(checked && !this.selectedSubOptions.includes(subOption)) this.selectedSubOptions.push(subOption);

        // if suboption unchecked, remove it from selection
        if(!checked) {
            if(this.selectedSubOptions.includes(subOption)) this.selectedSubOptions = this.selectedSubOptions.filter(so => so !== subOption);
            const foundOption = this.selectedProperty?.options.find(o => o === option);
            if(foundOption) {
                const foundSubOptions = this.selectedSubOptions.filter(so => foundOption[this.selectedProperty?.subOptionProperty!].includes(so) && so.isActive || this.showInactiveOptions);
                // if all suboptions for an option are unchecked, uncheck that option and remove it from selection
                if(!foundSubOptions.length) {
                    this.selectedOptions = this.selectedOptions.filter(o => o !== option);
                    if(optionCheckbox) optionCheckbox!.checked = false;
                }
            }
        }
        this.hasChanges();
    }

    ////****************** Functions ***********************************************////
    protected isOptionAvailable: (option: RuleConditionOption) => boolean = (option: RuleConditionOption) => {
        // option is available if it is not already used and if it is not excluded for the current event
        return option.excludeOnEvent ? !option.$used && option.excludeOnEvent !== this.event : !option.$used;
    }

    // filters out parent options that do not have active suboptions
    protected hasActiveSubOptions(option: any): boolean {
        if(!this.selectedProperty) return false;
        const { subOptionProperty } = this.selectedProperty;
        if(!subOptionProperty) return false;
        const subOptions: any[] = option[subOptionProperty];
        if(!subOptions.length) return false;
        return subOptions.some(so => so.isActive);
    }

    private clearSelectedOptions(): void {
        if(this.selectedOptions) this.selectedOptions.splice(0);
        if(this.selectedSubOptions) this.selectedSubOptions.splice(0);
    }

    private convertSelectionsIntoExpression(): boolean {
        if(!this.selectedProperty || !this.condition) return false;
        this.condition.option.expression = '';

        try {
            const { optionLevel1, optionLevel2, subOptionLevel1, subOptionLevel2, subOptionProperty } = this.selectedProperty;
            if(!subOptionProperty) {
                this.condition!.option.expression += `(new[] { `;
                this.condition.option.expression += this.selectedOptions.map(o => `"${o[optionLevel2]}"`).filter(o => !!o).join(', ');
                this.condition.option.expression += ` }.Any(x => root.${optionLevel1}.${optionLevel2} == x))`;
            } else {
                this.selectedOptions.forEach((o, index) => {
                    const foundSubOptions = this.selectedSubOptions.filter(so => o.hasOwnProperty(this.selectedProperty?.subOptionProperty) && o[this.selectedProperty?.subOptionProperty!].includes(so));
                    if(!foundSubOptions.length || !subOptionLevel1 || !subOptionLevel2) return;
                    this.condition!.option.expression += `(${optionLevel1}.${optionLevel2} eq "${o[optionLevel2]}" && new[] { `;
                    this.condition!.option.expression += foundSubOptions.map(so => `"${so[subOptionLevel2]}"`).filter(so => !!so).join(', ');
                    this.condition!.option.expression += ` }.Any(x => root.${subOptionLevel1}.${subOptionLevel2} == x))`;
                    if(index < this.selectedOptions.length - 1) this.condition!.option.expression += ` || `;
                });
                // if multiple selected options (e.g. multiple program groups and programs), wrap all in parentheses
                if(this.selectedOptions.length > 1) this.condition.option.expression = `(${this.condition.option.expression})`;
            };
        } catch(error) {
            throw new Error(`Failed to convert selections into a valid expression due to: ${error}`);
        }
        const foundOption = this.options.find(o => o.translatedDisplay === this.condition?.option.translatedDisplay);
        if(foundOption) foundOption.expression = this.condition.option.expression;
        return true;
    }

    ////****************** Service Calls ********************************************////

}
